import { VNode, h } from 'vue'

import { QField } from 'quasar'
import { quasarHandlers } from '../quasarHandlers'

/**
 * The QField shell every labelled control sits in: the stacked label, the auto-save hint and the
 * validation slots.
 *
 * It was an abstract class with two hooks — `__getFieldClass` and `__getControl` — that BsToggle and
 * BsEditor filled in by extending it. Those hooks are arguments here, which is the whole conversion:
 * both are now ordinary components that call this from their own `render` rather than inheriting one.
 *
 * `host` is the calling component, for its `$attrs`/`$slots` and the `useBsComponent` helpers it
 * returned from `setup`. The model value is read the way the class read `this.value`: BsToggle declares
 * that prop, BsEditor does not and passes its value through `$attrs` instead -- hence the `$attrs`
 * fallback rather than a bare `host.value`, which Vue 3 warns about on the component that has no
 * such prop ("Property \"value\" was accessed during render but is not defined on instance").
 */
export function renderBsField (
  host: any,
  fieldClass: string,
  control: VNode | undefined
) {
  const props = {
    borderless: true,
    // `componentValue` resolves the declared prop, `modelValue` (what `v-model` sends under Vue 3) and
    // `value` in that order, so the field shell shows a value however the caller bound it.
    value: host.componentValue,
    stackLabel: true,
    bottomSlots: true,
    hint: host.autoSaveMessage,
    hideBottomSpace: host.$attrs['hide-bottom-space'] !== void 0
  }

  // The scoped slots are what carry the validation message: QField only renders its bottom area
  // from an `error` slot (or an errorMessage prop), so without them a failed rule turned the field
  // red via the `error` prop and then dropped the message on the floor. Every other bs-* component
  // hands its control the same slots. [sc-26988]
  const fieldSlots = { ...host.getSlots() }
  // QField renders a `default` scoped slot INSTEAD of its children, so that one has to stay with
  // the control — handing it over here would replace the control with it.
  delete fieldSlots.default

  return h(QField, {
    class: ['bs-field ' + fieldClass, host.getClasses()],
    ...host.getRenderProps(props),
    ...quasarHandlers(host.getListeners())
  }, {
    ...fieldSlots,
    // The control goes in the default slot, not in a children array: Vue 3 takes one slots object,
    // and QField renders `default` INSTEAD of children anyway.
    default: () => [control]
  })
}
